/**
 * Перенос данных из карточки сделки в профиль дела (ТЗ FR-06).
 *
 * У заказчика при переводе сделки по стадиям заполняется чек-лист: ФИО, ИНН,
 * СНИЛС, серия и номер паспорта. Всё это же требуется заявлению — и вводить
 * второй раз бессмысленно, а главное вредно: два места ввода означают два
 * разных значения ИНН и отказ суда.
 *
 * ПОЛЯ ИЩУТСЯ ПО НАЗВАНИЮ, а не по коду. Коды пользовательских полей портал
 * присваивает сам (`UF_CRM_1234567890`), они разные на каждом портале и ничего
 * не говорят о содержимом. Названия же видит и задаёт сам заказчик — по ним
 * и сверяемся.
 *
 * ГРАНИЦА. Переносится только то, что нужно документам. Учётные данные
 * Госуслуг («ГУ-логин», «ГУ-пароль») не переносятся никогда: заявлению они
 * не нужны, а копия пароля в профиле дела — это ещё одно место, откуда он
 * может утечь. Суммы оплаты услуг тоже не переносятся: это расчёты с юристом,
 * а не обязательства должника перед кредиторами.
 */

import type { CaseProfile } from '@bd/domain';
import { call } from './rest.js';

/** Куда кладётся значение в профиле. */
export type ImportTarget =
  | 'last'
  | 'first'
  | 'middle'
  | 'inn'
  | 'snils'
  | 'passportSeries'
  | 'passportNumber'
  | 'birthDate'
  | 'birthPlace'
  | 'phone'
  | 'email'
  | 'registrationAddress';

interface TargetSpec {
  /** Названия полей, которые означают это значение */
  labels: string[];
  title: string;
  /** Приведение значения портала к виду, принятому в профиле */
  clean?: (value: string) => string;
}

const digits = (value: string) => value.replace(/\D/g, '');

/** СНИЛС печатается в форме приказа № 530 как 123-456-789 00. */
const snilsFormat = (value: string) => {
  const d = digits(value);
  return d.length === 11 ? `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6, 9)} ${d.slice(9)}` : value.trim();
};

const TARGETS: Record<ImportTarget, TargetSpec> = {
  last: { labels: ['фамилия'], title: 'Фамилия' },
  first: { labels: ['имя'], title: 'Имя' },
  middle: { labels: ['отчество'], title: 'Отчество' },
  inn: { labels: ['инн', 'инндолжника'], title: 'ИНН', clean: digits },
  snils: { labels: ['снилс'], title: 'СНИЛС', clean: snilsFormat },
  passportSeries: { labels: ['паспортсерия', 'серияпаспорта'], title: 'Серия паспорта', clean: digits },
  passportNumber: { labels: ['паспортномер', 'номерпаспорта'], title: 'Номер паспорта', clean: digits },
  birthDate: { labels: ['датарождения'], title: 'Дата рождения' },
  birthPlace: { labels: ['месторождения'], title: 'Место рождения' },
  phone: { labels: ['телефон'], title: 'Телефон' },
  email: { labels: ['email', 'почта', 'электроннаяпочта'], title: 'Электронная почта' },
  registrationAddress: { labels: ['адресрегистрации', 'адреспрописки'], title: 'Адрес регистрации' },
};

/**
 * Поля, которые НЕ переносятся, хотя в чек-листе есть. Перечислены явно,
 * чтобы отказ был виден в отчёте: «пропущено, потому что…», а не молчание.
 */
const EXCLUDED: Array<{ labels: string[]; reason: string }> = [
  {
    labels: ['гулогин', 'гупароль', 'логингосуслуги', 'парольгосуслуги', 'госуслугилогин', 'госуслугипароль'],
    reason: 'учётные данные Госуслуг — заявлению не нужны и в профиль дела не переносятся',
  },
  {
    labels: ['суммаивалюта', 'полученапредоплата', 'суммыследующихплатежей', 'датыследующихплатежей'],
    reason: 'расчёты по договору с юристом, а не обязательства перед кредиторами',
  },
];

/**
 * Сравнение названий: регистр, пробелы, знаки и «ё» различий не образуют.
 * «Паспорт.Серия», «паспорт серия» и «Паспорт  Серия» — одно и то же поле.
 */
export function normalizeLabel(label: string): string {
  return label
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/[^\p{L}\p{N}]/gu, '');
}

export interface FoundValue {
  target: ImportTarget;
  title: string;
  /** Название поля в портале — по нему юрист узнаёт источник */
  sourceLabel: string;
  value: string;
}

export interface SkippedValue {
  sourceLabel: string;
  reason: string;
}

export interface CrmSnapshot {
  found: FoundValue[];
  skipped: SkippedValue[];
}

interface PortalField {
  title?: string;
  listLabel?: string;
  formLabel?: string;
  type?: string;
}

const labelOf = (field: PortalField): string => field.title ?? field.formLabel ?? field.listLabel ?? '';

/** Значение поля портала строкой. Множественные поля берутся первым значением. */
function asText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return asText(value[0]);
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return asText(record.VALUE ?? record.value ?? '');
  }
  return String(value).trim();
}

/**
 * Разбор одной карточки: сопоставление названий полей с профилем.
 *
 * Выделено отдельно от обращений к порталу, чтобы проверять сопоставление
 * без портала: именно здесь легко ошибиться и утащить в профиль пароль.
 */
export function matchFields(
  fields: Record<string, PortalField>,
  values: Record<string, unknown>,
): CrmSnapshot {
  const found: FoundValue[] = [];
  const skipped: SkippedValue[] = [];
  const taken = new Set<ImportTarget>();

  for (const [name, field] of Object.entries(fields)) {
    const label = labelOf(field);
    if (!label) continue;

    const normalized = normalizeLabel(label);
    const text = asText(values[name]);

    const excluded = EXCLUDED.find((rule) => rule.labels.includes(normalized));
    if (excluded) {
      // Пропуск отмечается, даже если поле пустое: юрист должен видеть,
      // что оно не забыто, а исключено намеренно
      skipped.push({ sourceLabel: label, reason: excluded.reason });
      continue;
    }

    if (!text) continue;

    const entry = (Object.entries(TARGETS) as Array<[ImportTarget, TargetSpec]>).find(([, spec]) =>
      spec.labels.includes(normalized),
    );
    if (!entry) continue;

    const [target, spec] = entry;
    // Первое совпадение выигрывает: карточка сделки важнее связанной с ней
    if (taken.has(target)) continue;
    taken.add(target);

    found.push({ target, title: spec.title, sourceLabel: label, value: spec.clean ? spec.clean(text) : text });
  }

  return { found, skipped };
}

/** Чтение карточки портала: описание полей и значения. */
async function readEntity(entityTypeId: number, id: string | number): Promise<CrmSnapshot | null> {
  try {
    const [fields, item] = await Promise.all([
      call<{ fields: Record<string, PortalField> }>('crm.item.fields', { entityTypeId }),
      call<{ item?: Record<string, unknown> }>('crm.item.get', { entityTypeId, id }),
    ]);
    if (!item?.item) return null;
    return matchFields(fields?.fields ?? {}, item.item);
  } catch {
    // Недоступность карточки не мешает работе: данные вводятся вручную
    return null;
  }
}

/** Сделка, связанная с карточкой, — на случай, если дело ведётся не в ней. */
function linkedDealId(item: Record<string, unknown> | null): string | null {
  const value = item?.parentId2 ?? item?.dealId ?? item?.DEAL_ID;
  return value ? String(value) : null;
}

export const DEAL_ENTITY_TYPE_ID = 2;

/**
 * Что можно перенести в дело.
 *
 * Дела ведутся в сделках, и обычно достаточно самой карточки. Связанная
 * сделка просматривается на случай, если дело заведут смарт-процессом:
 * чек-лист останется на сделке.
 */
export async function readCrmSnapshot(
  entityTypeId: number,
  caseId: string,
  item: Record<string, unknown> | null,
): Promise<CrmSnapshot> {
  const own = (await readEntity(entityTypeId, caseId)) ?? { found: [], skipped: [] };

  const dealId = linkedDealId(item);
  if (!dealId || entityTypeId === DEAL_ENTITY_TYPE_ID) return own;

  const deal = (await readEntity(DEAL_ENTITY_TYPE_ID, dealId)) ?? { found: [], skipped: [] };

  // Своя карточка приоритетнее: значения из неё уже заняли свои места
  const taken = new Set(own.found.map((value) => value.target));
  return {
    found: [...own.found, ...deal.found.filter((value) => !taken.has(value.target))],
    skipped: [...own.skipped, ...deal.skipped],
  };
}

export interface ApplyResult {
  profile: CaseProfile;
  /** Что заполнено */
  filled: FoundValue[];
  /** Что не тронуто, потому что уже заполнено юристом (FR-06) */
  occupied: FoundValue[];
}

const isEmpty = (value: unknown): boolean => !String(value ?? '').trim();

/**
 * Перенос значений в профиль.
 *
 * Заполняются ТОЛЬКО пустые поля: введённое юристом не перезаписывается
 * (FR-06). Иначе повторный перенос затирал бы правки, сделанные после
 * заполнения чек-листа, — а правит юрист как раз потому, что в чек-листе
 * ошиблись.
 */
export function applyToProfile(profile: CaseProfile, found: FoundValue[]): ApplyResult {
  const next: CaseProfile = JSON.parse(JSON.stringify(profile)) as CaseProfile;
  const filled: FoundValue[] = [];
  const occupied: FoundValue[] = [];

  const put = (empty: boolean, assign: () => void, value: FoundValue) => {
    if (!empty) {
      occupied.push(value);
      return;
    }
    assign();
    filled.push(value);
  };

  for (const value of found) {
    switch (value.target) {
      case 'last':
        put(isEmpty(next.debtor.last), () => (next.debtor.last = value.value), value);
        break;
      case 'first':
        put(isEmpty(next.debtor.first), () => (next.debtor.first = value.value), value);
        break;
      case 'middle':
        put(isEmpty(next.debtor.middle), () => (next.debtor.middle = value.value), value);
        break;
      case 'inn':
        put(isEmpty(next.debtor.inn), () => (next.debtor.inn = value.value), value);
        break;
      case 'snils':
        put(isEmpty(next.debtor.snils), () => (next.debtor.snils = value.value), value);
        break;
      case 'passportSeries':
        put(isEmpty(next.debtor.idDocument.series), () => (next.debtor.idDocument.series = value.value), value);
        break;
      case 'passportNumber':
        put(isEmpty(next.debtor.idDocument.number), () => (next.debtor.idDocument.number = value.value), value);
        break;
      case 'birthDate':
        put(isEmpty(next.debtor.birthDate), () => (next.debtor.birthDate = toIsoDate(value.value)), value);
        break;
      case 'birthPlace':
        put(isEmpty(next.debtor.birthPlace), () => (next.debtor.birthPlace = value.value), value);
        break;
      case 'phone':
        put(isEmpty(next.debtor.phone), () => (next.debtor.phone = value.value), value);
        break;
      case 'email':
        put(isEmpty(next.debtor.email), () => (next.debtor.email = value.value), value);
        break;
      case 'registrationAddress':
        put(
          isEmpty(next.debtor.registrationAddress.full),
          () => {
            // Строка из портала не разбирается по частям: разбор адреса
            // означал бы обращение вовне, а адрес должника наружу не уходит
            next.debtor.registrationAddress = {
              ...next.debtor.registrationAddress,
              full: value.value,
              fullManual: true,
            };
          },
          value,
        );
        break;
    }
  }

  return { profile: next, filled, occupied };
}

/** Дата портала бывает и «2026-09-09T00:00:00+03:00», и «09.09.1993». */
function toIsoDate(value: string): string | null {
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

  const ru = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(value.trim());
  if (ru) return `${ru[3]}-${ru[2]}-${ru[1]}`;

  return null;
}
